/**
 * Pure interaction system for the runner: board proximity (in the X–Z plane),
 * the entangled-handle / scanner-beam puzzle, the win check, and phases.
 */
import type { SimState, SimInput, SimConfig, SimEvent, Phase } from './types.js';
import type { LevelDef, Handle } from '../level/types.js';
import { pointInBox } from './collision.js';

const ARM_REACH = 0.7;

function canReach(s: SimState, h: Handle, cfg: SimConfig): boolean {
  const p = s.player;
  const hh = (p.crouching ? cfg.crouchH : cfg.playerH) / 2;
  const horizontal =
    Math.abs(p.pos.x - h.x) <= cfg.handleReachRadius &&
    Math.abs(p.pos.z - h.z) <= cfg.handleReachRadius;
  const vertical = h.y <= p.pos.y + hh + ARM_REACH && h.y >= p.pos.y - hh - ARM_REACH;
  return horizontal && vertical;
}

export function updateInteraction(
  state: SimState,
  input: SimInput,
  level: LevelDef,
  cfg: SimConfig,
  events: SimEvent[],
): void {
  const p = state.player;

  // --- boards: roadside panels — trigger by forward proximity on the matching
  // side of the road (so the two fork panels read independently). ---
  let near = -1;
  let bestDz = cfg.boardReadRadius;
  for (let i = 0; i < level.boards.length; i++) {
    const b = level.boards[i]!;
    const dz = Math.abs(p.pos.z - b.z);
    if (dz > cfg.boardReadRadius) continue;
    const sameSide = b.x === 0 || (b.x < 0 ? p.pos.x <= 0.7 : p.pos.x >= -0.7);
    if (!sameSide) continue;
    if (dz < bestDz) {
      bestDz = dz;
      near = i;
    }
  }
  if (near !== state.nearBoard) {
    if (state.nearBoard !== -1) events.push({ type: 'board-exit', index: state.nearBoard });
    if (near !== -1) {
      events.push({ type: 'board-enter', index: near });
      state.boardsRead[near] = true;
    }
    state.nearBoard = near;
  }

  // --- entangled handle / beam puzzle ---
  state.nearHandle = state.handleCorner !== 'latched' && canReach(state, level.handleCorner, cfg);

  if (input.interact && state.handleCorner !== 'latched') {
    if (canReach(state, level.handleCorner, cfg)) {
      state.handleCorner = 'latched';
      state.handleHigh = 'latched'; // entanglement: the partner moves untouched
      state.beamActive = true;
      events.push({ type: 'handle-operate' });
      events.push({ type: 'beam-on' });
    } else if (
      Math.abs(p.pos.x - level.handleHigh.x) <= cfg.handleReachRadius &&
      Math.abs(p.pos.z - level.handleHigh.z) <= cfg.handleReachRadius
    ) {
      events.push({ type: 'handle-reject' });
    }
  }

  // --- win: stand in the active beam ---
  const b = level.beam;
  state.inBeam =
    state.beamActive &&
    pointInBox(
      p.pos.x,
      p.pos.z,
      p.pos.y,
      b.x,
      b.z,
      b.w / 2,
      b.d / 2,
      level.floorY,
      level.floorY + b.h,
    );
  if (state.inBeam && !state.complete) {
    state.complete = true;
    events.push({ type: 'complete' });
  }

  state.phase = computePhase(state, level);
}

function computePhase(state: SimState, level: LevelDef): Phase {
  if (state.complete) return 'complete';
  if (state.beamActive) return 'beamActive';
  if (state.player.pos.z >= level.gateZ - 10) return 'atGate';
  return 'run';
}
